"use client";

import React from "react";
import { Button, Tooltip, TooltipTrigger, TooltipContent } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { MAX_REGENERATION_ATTEMPTS } from "@/constants/regeneration";
import { logger } from "@/telemetry";

export interface RegenerateButtonProps {
  attempts?: number;
  maxAttempts?: number;
  onRegenerate: () => Promise<void>;
  isLoading: boolean;
  dataId?: string;
}

const RegenerateButton = React.forwardRef<React.ElementRef<typeof Button>, RegenerateButtonProps>(
  (
    { attempts = 0, maxAttempts = MAX_REGENERATION_ATTEMPTS, onRegenerate, isLoading, dataId },
    ref,
  ) => {
    const { t } = useTranslation();

    const hasReachedMaxAttempts = attempts >= maxAttempts;
    const isDisabled = isLoading || hasReachedMaxAttempts;
    const tooltipContent = hasReachedMaxAttempts
      ? t("Maximum retries reached")
      : isLoading
        ? t("Generating")
        : t("Try again");

    const handleRegenerate = async () => {
      if (isDisabled) return;

      try {
        await onRegenerate();
      } catch (error) {
        logger.error("Regeneration failed", error);
      }
    };

    return (
      <Tooltip>
        <TooltipTrigger>
          <Button
            ref={ref}
            isIconOnly
            aria-label={tooltipContent}
            className="regenerate-button"
            data-id={`regenerate-button-${dataId}`}
            isDisabled={isDisabled}
            isPending={isLoading}
            size="sm"
            variant="ghost"
            onPress={handleRegenerate}
          >
            {isLoading ? (
              <Icon
                className="text-lg text-default-600 animate-spin"
                icon="gravity-ui:arrows-rotate-left"
              />
            ) : (
              <Icon className="text-lg text-default-600" icon="gravity-ui:arrows-rotate-left" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    );
  },
);

RegenerateButton.displayName = "RegenerateButton";

export { RegenerateButton };
